/**
 * Event tracking — "דאשבורד" tab. Website analytics for a date range.
 *
 * Sections (top → bottom):
 *   header       quick ranges (7 / 14 days, month, 3 months) + one calendar pill: any two days,
 *                with presets beside the calendar (this week … 3 weeks ago, a month ago, 2 months, half a year,
 *                a year); picking applies at once. "● N באתר עכשיו" pill: last-30-minutes activity
 *                (refreshes every 30s, independent of the range; hover for the event breakdown).
 *   KPI row      unique visitors + daily average (▲▼ vs the same number of days right before —
 *                one extra getVisitorTrends call), conversions + rate of the main funnel.
 *   funnels      main funnel full width, the others in a grid. Per stage: visitors who reached it
 *                in order (bar), % of the first stage, % from the previous stage; hover → time on
 *                page / scroll depth for that stage's pages (engagement byEventType)
 *   trend        unique visitors per day, split new / returning
 *   engagement   scroll-depth distribution (all pages or one) + per-page table (pick pages)
 *   Google       Search Console keywords (only when the server has GSC enabled)
 *   referrers    where visitors came from
 *
 * Data (unchanged, all via props from EventTracking.js): getAllFunnelsStats · getVisitorTrends ·
 * getEngagementStats · getRealTimeStats(30) · getSearchKeywords · getReferrerStats — all reloaded
 * together when the range changes. Same date math as before (toISOString day strings).
 *
 * Look changes: the funnel bars used to change color by retention tier (purple/green/blue/yellow/
 * gray) and conversion % went red→green; now one blue for every stage (optional stages hatched)
 * and the percentages are written out. Scroll buckets use one blue ramp light→dark instead of
 * red→green. Colors validated with the dataviz palette checker (new/returning = blue/orange).
 */
import React, { useState, useEffect, useRef } from 'react';
import { ReloadOutlined, InfoCircleOutlined, CalendarOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  Section,
  StatCard,
  Metric,
  DataTable,
  EmptyState,
  Button,
  Select,
  Segmented,
  Tooltip,
  Popover,
  DatePicker,
  useBreakpoint,
} from '../../../ui';
import classes from './Dashboard.css';

const safeNumber = (value, defaultValue = 0) => {
  let num = Number(value);
  if (isNaN(num) || value === null || value === undefined) num = defaultValue;
  return num;
};
const formatNumber = (value, defaultValue) => safeNumber(value, defaultValue).toLocaleString();
const formatPercent = (value) => safeNumber(value, 0) + '%';
const formatTime = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.round(seconds);
  if (s < 60) return s + ' שנ׳';
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m + ' דק׳' + (rem > 0 ? ' ' + rem + ' שנ׳' : '');
};

// Chart colors (dataviz palette, validated): categorical slots 1–2 + one blue ordinal ramp
const C = { new: '#2a78d6', returning: '#eb6834', bar: '#2a78d6' };
const SCROLL_BUCKETS = [
  { key: '0-25', label: '0–25%', color: '#86b6ef' },
  { key: '25-50', label: '25–50%', color: '#5598e7' },
  { key: '50-75', label: '50–75%', color: '#2a78d6' },
  { key: '75-100', label: '75–100%', color: '#184f95' },
];
const bucketTotal = (dist) => SCROLL_BUCKETS.reduce((sum, b) => sum + ((dist && dist[b.key]) || 0), 0);

// Date math (unchanged from the old screen): day strings from toISOString, end = today.
const isoDay = (d) => d.toISOString().split('T')[0];
const getDateRange = (filterType, months) => {
  const end = new Date();
  const start = new Date();
  switch (filterType) {
    case 'week1': start.setDate(start.getDate() - 7); break;
    case 'week2': start.setDate(start.getDate() - 14); break;
    case 'month1': start.setMonth(start.getMonth() - 1); break;
    case 'month2': start.setMonth(start.getMonth() - 2); break;
    case 'month3': start.setMonth(start.getMonth() - 3); break;
    case 'custom_months': start.setMonth(start.getMonth() - months); break;
    default: start.setMonth(start.getMonth() - 1);
  }
  return { startDate: isoDay(start), endDate: isoDay(end) };
};
// "השבוע" / "שבוע שעבר"…: 7 days ending (weeksAgo - 1) weeks ago
const weekRange = (weeksAgo) => {
  const end = new Date();
  end.setDate(end.getDate() - (weeksAgo - 1) * 7);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return { startDate: isoDay(start), endDate: isoDay(end) };
};

// The four ranges people use most — one click, always visible
const QUICK = [
  { value: 'week1', label: '7 ימים' },
  { value: 'week2', label: '14 ימים' },
  { value: 'month1', label: 'חודש' },
  { value: 'month3', label: '3 חודשים' },
];
// Everything else lives next to the calendar (Ant RangePicker presets). key = activeQuickFilter value.
const PRESETS = [
  { key: 'week_compare_1', label: 'השבוע', range: () => weekRange(1) },
  { key: 'week_compare_2', label: 'שבוע שעבר', range: () => weekRange(2) },
  { key: 'week_compare_3', label: 'לפני שבועיים', range: () => weekRange(3) },
  { key: 'week_compare_4', label: 'לפני 3 שבועות', range: () => weekRange(4) },
  { key: 'week_compare_5', label: 'לפני חודש', range: () => weekRange(5) }, // the week 4 weeks ago
  { key: 'month2', label: 'חודשיים', range: () => getDateRange('month2') },
  { key: 'custom_months_6', label: 'חצי שנה', range: () => getDateRange('custom_months', 6) },
  { key: 'custom_months_12', label: 'שנה', range: () => getDateRange('custom_months', 12) },
];
const ALL_RANGES = [...QUICK.map((q) => ({ key: q.value, range: () => getDateRange(q.value) })), ...PRESETS];

// "22 בספט׳" — the year only when it isn't this year
const dayLabel = (d) => d.format(d.year() === dayjs().year() ? 'D MMM' : 'D MMM YY');
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

// Change vs the previous period of the same length: ▲ 12% / ▼ 4% (null when there's nothing to compare)
const Delta = ({ now, before }) => {
  if (!before || !now) return null;
  const pct = Math.round(((now - before) / before) * 100);
  if (!isFinite(pct)) return null;
  const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  return (
    <Tooltip title={`בתקופה הקודמת: ${formatNumber(before)}`}>
      <span className={classes.delta} data-dir={dir}>
        {dir === 'up' ? <ArrowUpOutlined /> : dir === 'down' ? <ArrowDownOutlined /> : null}
        {Math.abs(pct)}%
      </span>
    </Tooltip>
  );
};

const ALL_PAGES = '__all__'; // scroll selector value for "all pages" (state keeps null, as before)
const ALL_SITE = '__site__'; // trend chart value for "the whole site" (state keeps null)

// Round axis step (1/2/5 × 10ⁿ, whole numbers) so ~4 grid lines cover max
const niceStep = (max, lines = 4) => {
  const raw = max / lines;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / p;
  return Math.max(1, (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p);
};

// ---- small chart pieces ---------------------------------------------------------------

// Horizontal distribution bars (scroll depth): label · bar · count
const DistBars = ({ dist, unit }) => {
  const total = bucketTotal(dist);
  return (
    <div className={classes.dist}>
      {SCROLL_BUCKETS.map((b) => {
        const count = (dist && dist[b.key]) || 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div key={b.key} className={classes.distRow}>
            <span className={classes.distLabel}>{b.label}</span>
            <div className={classes.distTrack}>
              <span className={classes.distFill} style={{ width: `${Math.max(pct, count ? 2 : 0)}%`, background: b.color }} />
            </div>
            <span className={classes.distValue}>
              {pct}% <span className={classes.distCount}>· {formatNumber(count)}{unit ? ` ${unit}` : ''}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Sections stay on the page when a range has no data (they used to disappear, which looked like a bug)
const NoData = ({ text }) => <div className={classes.noData}>{text || 'אין נתונים לטווח התאריכים הנבחר'}</div>;

const Dashboard = (props) => {
  const [funnelsData, setFunnelsData] = useState([]);
  const [realTimeData, setRealTimeData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  // Visitor trend chart: null = the whole site, else a funnel id (counts visitors on its first stage)
  const [trendFunnelId, setTrendFunnelId] = useState(null);
  const [funnelTrendData, setFunnelTrendData] = useState(null);
  const [funnelTrendLoading, setFunnelTrendLoading] = useState(false);
  const funnelTrendReq = useRef(0); // only the latest request may set the chart (fast switching, range reloads)
  const [engagementData, setEngagementData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeQuickFilter, setActiveQuickFilter] = useState('month1');
  const [prevTrends, setPrevTrends] = useState(null); // same-length period before the range (for ▲▼)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [realTimeMinutes] = useState(30);

  const [searchKeywordsData, setSearchKeywordsData] = useState(null);
  const [referrerData, setReferrerData] = useState(null);
  const [gscExpanded, setGscExpanded] = useState(false);

  // page engagement selector (null = not yet initialized → top 3)
  const [selectedPages, setSelectedPages] = useState(null);
  // scroll distribution page (null = all pages)
  const [selectedScrollPage, setSelectedScrollPage] = useState(null);
  const screens = useBreakpoint();

  useEffect(() => {
    applyQuickFilter('month1');
    const interval = setInterval(() => loadRealTimeData(), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize selectedPages to top-3 on first data load
  useEffect(() => {
    if (engagementData && engagementData.byPage && selectedPages === null) {
      setSelectedPages(engagementData.byPage.slice(0, 3).map((p) => p.pageUrl));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementData]);

  // ---- date ranges ----
  const applyRange = (key, range) => {
    setActiveQuickFilter(key);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    loadAllData(range.startDate, range.endDate);
  };

  const applyQuickFilter = (filterType) => applyRange(filterType, getDateRange(filterType));

  // Calendar: a preset or any two days. Remember which preset it was (if any) for the highlight.
  const applyPicked = (dates) => {
    if (!dates || !dates[0] || !dates[1]) return;
    const range = { startDate: dates[0].format('YYYY-MM-DD'), endDate: dates[1].format('YYYY-MM-DD') };
    const match = ALL_RANGES.find((r) => {
      const x = r.range();
      return x.startDate === range.startDate && x.endDate === range.endDate;
    });
    applyRange(match ? match.key : 'custom', range);
  };

  // ---- loading (unchanged) ----
  const loadAllData = async (start, end) => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadFunnelsData(start, end),
        loadTrendsData(start, end),
        loadFunnelTrend(start, end, trendFunnelId),
        loadEngagementData(start, end),
        loadRealTimeData(),
        loadSearchKeywords(start, end),
        loadReferrerStats(start, end),
        loadPrevTrends(start, end),
      ]);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('שגיאה בטעינת נתונים');
    }
    setLoading(false);
  };

  const loadFunnelsData = async (start, end) => {
    try {
      const data = await props.getAllFunnelsStats(start || null, end || null);
      if (data && Array.isArray(data.funnels)) setFunnelsData(data.funnels);
      else if (data && Array.isArray(data)) setFunnelsData(data);
      else setFunnelsData([]);
    } catch (err) {
      console.error('Error loading funnels data:', err);
      setFunnelsData([]);
    }
  };

  // The same number of days right before the range — only to show ▲▼ on the headline numbers
  const loadPrevTrends = async (start, end) => {
    setPrevTrends(null);
    if (!start || !end) return;
    const len = daysBetween(start, end);
    const prevEnd = new Date(start);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - len);
    try {
      const data = await props.getVisitorTrends(isoDay(prevStart), isoDay(prevEnd));
      if (data && data.summary) setPrevTrends(data.summary);
    } catch (err) {
      // no comparison — the numbers still show
    }
  };

  const loadTrendsData = async (start, end) => {
    try {
      const data = await props.getVisitorTrends(start || null, end || null);
      if (data && data.daily && Array.isArray(data.daily)) {
        if (!data.summary) data.summary = { totalVisitors: 0, avgVisitorsPerDay: 0, totalEvents: 0 };
        setTrendsData(data);
      } else {
        setTrendsData(null);
      }
    } catch (err) {
      console.error('Error loading trends data:', err);
      setTrendsData(null);
    }
  };

  // The trend chart for one funnel: visitors per day on the funnel's first stage.
  // Separate from trendsData, which also feeds the headline numbers (whole site).
  const loadFunnelTrend = async (start, end, funnelId, funnels = funnelsData) => {
    const funnel = funnelId ? funnels.find((f) => f.funnelId === funnelId) : null;
    const entry = funnel && funnel.stages && funnel.stages[0];
    const req = ++funnelTrendReq.current;
    if (!entry) {
      setFunnelTrendData(null);
      setFunnelTrendLoading(false);
      return;
    }
    setFunnelTrendLoading(true);
    let data = null;
    try {
      data = await props.getVisitorTrends(start || null, end || null, entry.eventType);
    } catch (err) {
      console.error('Error loading funnel trend:', err);
    }
    if (req !== funnelTrendReq.current) return; // a newer pick/range already asked
    setFunnelTrendData(data && Array.isArray(data.daily) ? data : null);
    setFunnelTrendLoading(false);
  };

  const onTrendFunnelChange = (v) => {
    const funnelId = v === ALL_SITE ? null : v;
    setTrendFunnelId(funnelId);
    setFunnelTrendData(null);
    loadFunnelTrend(startDate, endDate, funnelId);
  };

  const loadSearchKeywords = async (start, end) => {
    if (!props.getSearchKeywords) return;
    try {
      const data = await props.getSearchKeywords(start || null, end || null);
      setSearchKeywordsData(data);
    } catch (err) {
      console.error('[GSC] request failed:', err.message, err.response && err.response.status);
    }
  };

  const loadReferrerStats = async (start, end) => {
    if (!props.getReferrerStats) return;
    try {
      const data = await props.getReferrerStats(start || null, end || null);
      setReferrerData(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading referrer data:', err);
      setReferrerData([]);
    }
  };

  const loadEngagementData = async (start, end) => {
    if (!props.getEngagementStats) return;
    try {
      const data = await props.getEngagementStats(start || null, end || null);
      if (data) setEngagementData(data);
    } catch (err) {
      console.error('Error loading engagement data:', err);
    }
  };

  const loadRealTimeData = async () => {
    try {
      const data = await props.getRealTimeStats(realTimeMinutes);
      if (data && data.stats) {
        data.stats.uniqueVisitors = safeNumber(data.stats.uniqueVisitors, 0);
        data.stats.totalEvents = safeNumber(data.stats.totalEvents, 0);
        if (!data.period) data.period = { minutes: realTimeMinutes };
        setRealTimeData(data);
      } else {
        setRealTimeData(null);
      }
    } catch (err) {
      console.error('Error loading real-time data:', err);
      setRealTimeData(null);
    }
  };

  // ---- header: range control · live pill ----------------------------------------------
  const pickerValue = startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null;
  const quickValue = QUICK.some((q) => q.value === activeQuickFilter) ? activeQuickFilter : null;
  const activePreset = PRESETS.find((p) => p.key === activeQuickFilter);

  const renderHeader = () => (
    <div className={classes.header}>
      <div className={classes.rangeGroup}>
        <Segmented value={quickValue} onChange={applyQuickFilter} options={QUICK} className={classes.quick} />
        {/* The calendar button: reads like a label ("22 בספט׳ – 22 באוק׳"), opens presets + two months */}
        <div className={classes.rangeButton} data-active={!quickValue || undefined} onClick={(e) => {
            // clicks inside the calendar popup bubble here through the React tree — only the pill itself opens it
            if (e.currentTarget.contains(e.target)) setPickerOpen(true);
          }}>
          <CalendarOutlined className={classes.rangeIcon} />
          {activePreset ? <span className={classes.rangePreset}>{activePreset.label}</span> : null}
          <DatePicker.RangePicker
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            value={pickerValue}
            onChange={applyPicked}
            format={dayLabel}
            separator={<span className={classes.rangeSep}>–</span>}
            allowClear={false}
            inputReadOnly
            variant="borderless"
            suffixIcon={null}
            disabledDate={(d) => d.isAfter(dayjs(), 'day')}
            presets={PRESETS.map((p) => {
              const r = p.range();
              return { label: p.label, value: [dayjs(r.startDate), dayjs(r.endDate)] };
            })}
            popupClassName={classes.rangePopup}
            className={classes.rangePicker}
          />
        </div>
      </div>

      <div className={classes.headerEnd}>
        {renderLive()}
        <Tooltip title="רענון">
          <Button
            type="text"
            icon={<ReloadOutlined spin={loading} />}
            aria-label="רענון"
            onClick={() => loadAllData(startDate, endDate)}
            className={classes.refresh}
          />
        </Tooltip>
      </div>
    </div>
  );

  // "● 7 באתר עכשיו" — hover/tap for what they are doing
  const renderLive = () => {
    if (!realTimeData) return null;
    const periodMinutes = safeNumber((realTimeData.period || {}).minutes, realTimeMinutes);
    const stats = realTimeData.stats || {};
    const breakdown = Array.isArray(realTimeData.breakdown) ? realTimeData.breakdown : [];
    const visitors = safeNumber(stats.uniqueVisitors, 0);
    const content = (
      <div className={classes.livePanel}>
        <div className={classes.livePanelHead}>
          <span className="ui-tabular">{formatNumber(stats.totalEvents)}</span> אירועים ב-{periodMinutes} הדקות האחרונות
        </div>
        {breakdown.length === 0 ? (
          <div className={classes.muted}>אין פעילות כרגע</div>
        ) : (
          <ul className={classes.liveList}>
            {breakdown.slice(0, 10).map((item, index) => (
              <li key={(item && item.eventType) || index}>
                <code dir="ltr">{(item && item.eventType) || 'event-' + index}</code>
                <span className="ui-tabular">{formatNumber(item && item.totalEvents)}</span>
                <span className={classes.muted}>{formatNumber(item && item.uniqueVisitors)} מב׳</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
    return (
      <Popover content={content} title="פעילות בזמן אמת" placement="bottomLeft" trigger={['hover', 'click']}>
        <button type="button" className={classes.livePill} data-live={visitors > 0 || undefined}>
          <span className={classes.liveDot} />
          <span className="ui-tabular">{formatNumber(visitors)}</span>
          <span>באתר עכשיו</span>
        </button>
      </Popover>
    );
  };

  // ---- headline numbers ------------------------------------------------------------------
  const renderKpis = () => {
    const summary = (trendsData && trendsData.summary) || null;
    const main = funnelsData.find((f) => f.isMain) || funnelsData[0];
    const fs_ = (main && main.summary) || null;
    const days = startDate && endDate ? daysBetween(startDate, endDate) : null;
    const items = [
      {
        key: 'visitors',
        label: 'מבקרים ייחודיים',
        value: summary ? formatNumber(summary.totalUniqueVisitors) : '—',
        delta: summary && prevTrends ? <Delta now={safeNumber(summary.totalUniqueVisitors)} before={safeNumber(prevTrends.totalUniqueVisitors)} /> : null,
      },
      {
        key: 'avg',
        label: 'ממוצע ליום',
        value: summary ? formatNumber(summary.avgVisitorsPerDay) : '—',
        delta: summary && prevTrends ? <Delta now={safeNumber(summary.avgVisitorsPerDay)} before={safeNumber(prevTrends.avgVisitorsPerDay)} /> : null,
      },
      { key: 'conv', label: main ? `המרות · ${main.funnelName || 'משפך'}` : 'המרות', value: fs_ ? formatNumber(fs_.totalConversions) : '—' },
      { key: 'rate', label: 'אחוז המרה', value: fs_ ? formatPercent(fs_.overallConversionRate) : '—', accent: true },
    ];
    return (
      <div className={classes.kpis}>
        {items.map((k) => (
          <div key={k.key} className={classes.kpi}>
            <span className={classes.kpiLabel} title={k.label}>
              {k.label}
            </span>
            <span className={classes.kpiValueRow}>
              <span className={classes.kpiValue} data-accent={k.accent || undefined}>
                {k.value}
              </span>
              {k.delta}
            </span>
          </div>
        ))}
        {days ? <span className={classes.kpiNote}>השוואה ל-{days} הימים שלפני</span> : null}
      </div>
    );
  };

  // ---- funnels ----------------------------------------------------------------------------
  const renderEngagementCard = (eventType) => {
    const eng = engagementData && engagementData.byEventType && engagementData.byEventType[eventType];
    if (!eng) return null;
    const total = bucketTotal(eng.scrollDistribution);
    return (
      <div className={classes.engCard}>
        <div className={classes.engMetrics}>
          <Metric value={formatTime(eng.avgTimeSeconds)} label="זמן ממוצע בדף" />
          <Metric value={eng.avgScrollPct != null ? eng.avgScrollPct + '%' : '—'} label="גלילה ממוצעת" />
        </div>
        {total > 0 ? (
          <>
            <div className={classes.engSub}>עומק גלילה ({formatNumber(total)} אירועים)</div>
            <DistBars dist={eng.scrollDistribution} />
          </>
        ) : (
          <div className={classes.muted}>אין נתוני מעורבות לשלב זה</div>
        )}
      </div>
    );
  };

  const renderFunnelChart = (funnelData, isCompact) => {
    if (!funnelData || !funnelData.stages || !Array.isArray(funnelData.stages)) return null;
    if (funnelData.stages.length === 0) return <EmptyState title="לא הוגדרו שלבים למשפך זה" />;

    const maxVisitors = Math.max(1, ...funnelData.stages.map((s) => safeNumber(s && s.funnelVisitors, 0)));
    const firstFunnelVisitors = safeNumber(funnelData.stages[0] && funnelData.stages[0].funnelVisitors, 0);
    const summary = funnelData.summary || {};

    return (
      <div className={classes.funnel}>
        <div className={classes.stages}>
          {funnelData.stages.map((stage, index) => {
            const funnelVisitors = safeNumber(stage && stage.funnelVisitors, 0);
            const totalStageVisitors = safeNumber(stage && stage.totalVisitors, funnelVisitors);
            const stageName = (stage && stage.name) || 'שלב ' + (index + 1);
            const stageEventType = (stage && stage.eventType) || 'stage-' + index;
            const isMandatory = stage && stage.isMandatory !== false;
            const conversionRate = stage && stage.conversionRate;
            const retention = firstFunnelVisitors > 0 ? Math.min(100, Math.round((funnelVisitors / firstFunnelVisitors) * 100)) : 0;
            const engagement = renderEngagementCard(stageEventType);

            const row = (
              <div className={classes.stage} data-optional={!isMandatory || undefined}>
                <div className={classes.stageHead}>
                  <span className={classes.stageName}>
                    {stageName}
                    {!isMandatory ? <span className={classes.optional}>אופציונלי</span> : null}
                    {engagement && !isCompact ? <InfoCircleOutlined className={classes.stageInfo} /> : null}
                  </span>
                  <span className={classes.stageNums}>
                    {index > 0 && conversionRate !== null && conversionRate !== undefined ? (
                      <Tooltip title="מהשלב הקודם">
                        <span className={classes.stepRate}>↓ {formatPercent(conversionRate)}</span>
                      </Tooltip>
                    ) : null}
                    {index > 0 && isMandatory ? (
                      <Tooltip title="מהשלב הראשון">
                        <span className={classes.retention}>{retention}% מההתחלה</span>
                      </Tooltip>
                    ) : null}
                  </span>
                </div>
                {/* the count sits right at the end of its bar (track leaves room for it) */}
                <div className={classes.stageTrack}>
                  <span className={classes.stageBar} style={{ width: `${Math.max((funnelVisitors / maxVisitors) * 100, funnelVisitors ? 1.5 : 0)}%` }} />
                  <span className={classes.stageCount}>{formatNumber(funnelVisitors)}</span>
                </div>
                {totalStageVisitors !== funnelVisitors ? (
                  <div className={classes.stageTotal}>
                    {formatNumber(totalStageVisitors)} הגיעו לשלב בסך הכל (גם לא לפי הסדר)
                  </div>
                ) : null}
              </div>
            );

            return engagement ? (
              <Popover key={stageEventType + '-' + index} content={engagement} title={`מעורבות · ${stageName}`} placement="top" mouseEnterDelay={0.15}>
                {row}
              </Popover>
            ) : (
              <React.Fragment key={stageEventType + '-' + index}>{row}</React.Fragment>
            );
          })}
        </div>

        <div className={classes.funnelSummary}>
          <Metric value={formatNumber(summary.totalVisitors)} label='סה"כ מבקרים' />
          <Metric value={formatNumber(summary.totalConversions)} label="המרות" />
          <Metric value={formatPercent(summary.overallConversionRate)} label="אחוז המרה" color="var(--ui-accent)" />
        </div>
      </div>
    );
  };

  const renderFunnelCard = (funnelData, isMain) => (
    <StatCard
      key={funnelData.funnelId}
      title={
        <span className={classes.funnelTitle}>
          {funnelData.funnelName || 'משפך המרה'}
          {isMain ? <span className={classes.mainBadge}>ראשי</span> : null}
        </span>
      }
    >
      {funnelData.message ? <p className={classes.muted}>{funnelData.message}</p> : null}
      {renderFunnelChart(funnelData, !isMain)}
    </StatCard>
  );

  const renderFunnels = () => {
    if (funnelsData.length === 0) {
      return (
        <Section>
          <EmptyState title="לא הוגדרו משפכים" description='עברו ללשונית "הגדרת משפכים" כדי להוסיף משפך' />
        </Section>
      );
    }
    const mainFunnel = funnelsData.find((f) => f.isMain);
    const secondaryFunnels = funnelsData.filter((f) => !f.isMain);
    return (
      <>
        {mainFunnel ? renderFunnelCard(mainFunnel, true) : null}
        {secondaryFunnels.length > 0 ? <div className={classes.secondaryGrid}>{secondaryFunnels.map((f) => renderFunnelCard(f, false))}</div> : null}
      </>
    );
  };

  // ---- visitor trend (stacked new / returning) ---------------------------------------
  const renderTrendsChart = () => {
    const funnel = trendFunnelId ? funnelsData.find((f) => f.funnelId === trendFunnelId) : null;
    const data = funnel ? funnelTrendData : trendsData;
    // Whole site, or one funnel (its first stage = "entered the funnel")
    const picker =
      funnelsData.length > 0 ? (
        <Select
          className={classes.pageSelect}
          value={funnel ? funnel.funnelId : ALL_SITE}
          onChange={onTrendFunnelChange}
          options={[
            { value: ALL_SITE, label: 'כל האתר' },
            ...funnelsData
              .filter((f) => f.funnelId && f.stages && f.stages.length > 0)
              .map((f) => ({ value: f.funnelId, label: `${f.funnelName} · ${f.stages[0].name}` })),
          ]}
        />
      ) : null;
    if (!data || !Array.isArray(data.daily) || data.daily.length === 0) {
      return (
        <StatCard title="מגמת מבקרים">
          {picker}
          <NoData text={funnel && funnelTrendLoading ? 'טוען…' : funnel ? 'אין כניסות למשפך בטווח התאריכים הנבחר' : 'אין ביקורים באתר בטווח התאריכים הנבחר'} />
        </StatCard>
      );
    }
    const peak = Math.max(1, ...data.daily.map((d) => safeNumber(d && d.uniqueVisitors, 0)));
    // bars scale to the top grid line, so the lines read as counts
    const step = niceStep(peak);
    const maxVisitors = step * Math.ceil(peak / step);
    const ticks = [];
    for (let v = step; v <= maxVisitors; v += step) ticks.push(v);
    const many = data.daily.length > 31;
    // date labels: ~15 across on desktop, ~6 on phones (they'd overlap otherwise)
    const labelEvery = Math.ceil(data.daily.length / (screens.md === false ? 6 : 15));

    return (
      <StatCard
        title="מגמת מבקרים"
        extra={
          <span className={classes.legend}>
            <span className={classes.legendItem}>
              <span className={classes.swatch} style={{ background: C.new }} />
              חדשים
            </span>
            <span className={classes.legendItem}>
              <span className={classes.swatch} style={{ background: C.returning }} />
              חוזרים
            </span>
          </span>
        }
      >
        {picker}
        <div className={classes.trendChart}>
          <div className={classes.trendAxis} aria-hidden="true">
            <span className={classes.trendAxisSizer}>{formatNumber(maxVisitors)}</span>
            <span style={{ bottom: 0 }}>0</span>
            {ticks.map((v) => (
              <span key={v} style={{ bottom: `${(v / maxVisitors) * 100}%` }}>
                {formatNumber(v)}
              </span>
            ))}
          </div>
          <div className={classes.trendPlot}>
            <div className={classes.trendGrid} aria-hidden="true">
              {ticks.map((v) => (
                <span key={v} style={{ bottom: `${(v / maxVisitors) * 100}%` }} />
              ))}
            </div>
            <div className={classes.trend} data-dense={many || undefined} role="img" aria-label="מבקרים ביום, חדשים וחוזרים">
              {data.daily.map((day, index) => {
                const total = safeNumber(day && day.uniqueVisitors, 0);
                const fresh = safeNumber(day && day.newVisitors, 0);
                const back = safeNumber(day && day.returningVisitors, 0);
                const events = safeNumber(day && day.totalEvents, 0);
                const dateStr = (day && day.date) || '';
                const dateObj = new Date(dateStr);
                const valid = !isNaN(dateObj.getTime());
                const label = valid ? dateObj.getDate() + '/' + (dateObj.getMonth() + 1) : '-';
                const h = (total / maxVisitors) * 100;
                // new + returning can differ from unique by a little; split the bar by their ratio
                const freshShare = fresh + back > 0 ? fresh / (fresh + back) : 1;
                return (
                  <Tooltip
                    key={dateStr || index}
                    mouseEnterDelay={0}
                    title={
                      <div className={classes.tip}>
                        <div className={classes.tipTitle}>{valid ? dateObj.toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'numeric' }) : dateStr}</div>
                        <div>{formatNumber(total)} ייחודיים</div>
                        <div>
                          <span className={classes.swatch} style={{ background: C.new }} /> {formatNumber(fresh)} חדשים
                        </div>
                        <div>
                          <span className={classes.swatch} style={{ background: C.returning }} /> {formatNumber(back)} חוזרים
                        </div>
                        <div className={classes.tipMuted}>{formatNumber(events)} אירועים</div>
                      </div>
                    }
                  >
                    <div className={classes.trendCol}>
                      <div className={classes.trendTrack}>
                        <div className={classes.trendStack} style={{ height: total ? `${Math.max(h, 1.5)}%` : 0 }}>
                          {freshShare < 1 ? <span style={{ flexGrow: 1 - freshShare, background: C.returning }} /> : null}
                          {freshShare > 0 ? <span style={{ flexGrow: freshShare, background: C.new }} /> : null}
                        </div>
                      </div>
                      <span className={classes.trendLabel}>{index % labelEvery === 0 ? label : ''}</span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        </div>
      </StatCard>
    );
  };

  // ---- engagement ---------------------------------------------------------------------
  const renderScrollDistribution = () => {
    if (!engagementData) return null; // still loading
    const pagesWithData = (engagementData.byPage || []).filter((p) => bucketTotal(p.scrollDistribution) > 0);
    const activeData = selectedScrollPage === null ? engagementData.overall : pagesWithData.find((p) => p.pageUrl === selectedScrollPage);
    if (!activeData) {
      return (
        <StatCard title="התפלגות עומק גלילה">
          <NoData text="אין נתוני גלילה בטווח התאריכים הנבחר" />
        </StatCard>
      );
    }
    const total = bucketTotal(activeData.scrollDistribution);
    if (total === 0 && selectedScrollPage === null) {
      return (
        <StatCard title="התפלגות עומק גלילה">
          <NoData text="אין נתוני גלילה בטווח התאריכים הנבחר" />
        </StatCard>
      );
    }

    return (
      <StatCard title="התפלגות עומק גלילה">
        {pagesWithData.length > 0 ? (
          <Select
            className={classes.pageSelect}
            value={selectedScrollPage === null ? ALL_PAGES : selectedScrollPage}
            onChange={(v) => setSelectedScrollPage(v === ALL_PAGES ? null : v)}
            showSearch
            optionFilterProp="label"
            options={[
              { value: ALL_PAGES, label: 'כל הדפים' },
              ...pagesWithData.map((p) => ({ value: p.pageUrl, label: p.pageUrl, visitors: p.uniqueVisitors })),
            ]}
            optionRender={(opt) =>
              opt.value === ALL_PAGES ? (
                opt.label
              ) : (
                <span className={classes.pageOption}>
                  <span dir="ltr" className={classes.pageOptionUrl}>{opt.label}</span>
                  <span className={classes.muted}>{formatNumber(opt.data.visitors)}</span>
                </span>
              )
            }
          />
        ) : null}
        {total > 0 ? <DistBars dist={activeData.scrollDistribution} unit="מבקרים" /> : <p className={classes.muted}>אין נתוני גלילה לדף זה</p>}
        {activeData.avgScrollPct != null ? (
          <div className={classes.cardFoot}>
            ממוצע גלילה <strong>{activeData.avgScrollPct}%</strong> · זמן ממוצע בדף <strong>{formatTime(activeData.avgTimeSeconds)}</strong> ·{' '}
            {formatNumber(total)} אירועים
          </div>
        ) : null}
      </StatCard>
    );
  };

  const renderPageEngagement = () => {
    if (!engagementData) return null; // still loading
    if (!engagementData.byPage || engagementData.byPage.length === 0) {
      return (
        <StatCard title="מעורבות לפי דף">
          <NoData text="אין צפיות בדפים בטווח התאריכים הנבחר" />
        </StatCard>
      );
    }
    const allPages = engagementData.byPage;
    const activeUrls = selectedPages || allPages.slice(0, 3).map((p) => p.pageUrl);
    const tableRows = allPages.filter((p) => activeUrls.indexOf(p.pageUrl) !== -1);

    return (
      <StatCard title="מעורבות לפי דף">
        <Select
          mode="multiple"
          className={classes.pageSelect}
          value={activeUrls}
          onChange={setSelectedPages}
          maxTagCount="responsive"
          placeholder="בחרו דפים"
          optionFilterProp="label"
          options={allPages.map((p) => ({ value: p.pageUrl, label: p.pageUrl }))}
          dropdownRender={(menu) => (
            <>
              <div className={classes.selectActions}>
                <Button size="small" type="link" onClick={() => setSelectedPages(allPages.map((p) => p.pageUrl))}>
                  בחר הכל
                </Button>
                <Button size="small" type="link" onClick={() => setSelectedPages([])}>
                  נקה הכל
                </Button>
              </div>
              {menu}
            </>
          )}
        />
        <DataTable
          data={tableRows}
          rowKey="pageUrl"
          fit
          pagination={false}
          empty={<EmptyState title="לא נבחרו דפים להצגה" />}
          columns={[
            {
              key: 'page',
              title: 'דף',
              render: (_, p) => (
                <Tooltip title={<span dir="ltr">{p.pageUrl}</span>} mouseEnterDelay={0.4}>
                  <span className={classes.url} dir="ltr">
                    {p.pageUrl}
                  </span>
                </Tooltip>
              ),
            },
            { key: 'visitors', title: 'מבקרים', width: 90, render: (_, p) => <span className="ui-tabular">{formatNumber(p.uniqueVisitors)}</span> },
            { key: 'time', title: 'זמן ממוצע', width: 110, responsive: ['sm'], render: (_, p) => formatTime(p.avgTimeSeconds) },
            {
              key: 'scroll',
              title: 'גלילה',
              width: 120,
              render: (_, p) =>
                p.avgScrollPct != null ? (
                  <span className={classes.miniBar}>
                    <span className="ui-tabular">{p.avgScrollPct}%</span>
                    <span className={classes.miniTrack}>
                      <span className={classes.miniFill} style={{ width: p.avgScrollPct + '%' }} />
                    </span>
                  </span>
                ) : (
                  '—'
                ),
            },
          ]}
        />
      </StatCard>
    );
  };

  // ---- Google Search Console ---------------------------------------------------------
  const renderSearchKeywords = () => {
    if (!searchKeywordsData || searchKeywordsData.enabled === false) return null;
    const rows = searchKeywordsData.rows || [];
    const hasError = !!searchKeywordsData.error;
    return (
      <StatCard title="חיפושים ב-Google" extra={!hasError && rows.length > 0 ? <span className={classes.muted}>{rows.length} מילות מפתח</span> : null}>
        {hasError ? (
          <div className={classes.errorNote} dir="auto">
            {searchKeywordsData.error}
          </div>
        ) : rows.length === 0 ? (
          <NoData text="אין נתוני חיפוש לטווח התאריכים הנבחר" />
        ) : (
          <>
            <DataTable
              data={gscExpanded ? rows : rows.slice(0, 5)}
              rowKey={(r) => r.keyword}
              fit
              pagination={false}
              columns={[
                { key: 'keyword', title: 'מילת חיפוש', render: (_, r) => <span className={classes.keyword}>{r.keyword}</span> },
                { key: 'clicks', title: 'קליקים', width: 90, render: (_, r) => <span className="ui-tabular">{formatNumber(r.clicks)}</span> },
                { key: 'impressions', title: 'חשיפות', width: 100, responsive: ['sm'], render: (_, r) => <span className="ui-tabular">{formatNumber(r.impressions)}</span> },
                { key: 'ctr', title: 'CTR', width: 80, responsive: ['sm'], render: (_, r) => <span className="ui-tabular">{r.ctr}%</span> },
                {
                  key: 'position',
                  title: 'מיקום',
                  width: 80,
                  render: (_, r) => (
                    <span className={classes.position} data-tier={r.position <= 3 ? 'top' : r.position <= 10 ? 'mid' : 'low'}>
                      {r.position}
                    </span>
                  ),
                },
              ]}
            />
            {rows.length > 5 ? (
              <Button type="link" size="small" onClick={() => setGscExpanded((v) => !v)} className={classes.more}>
                {gscExpanded ? 'הצג פחות' : `הצג הכל (${rows.length})`}
              </Button>
            ) : null}
          </>
        )}
      </StatCard>
    );
  };

  // ---- referrers ---------------------------------------------------------------------
  const renderReferrerStats = () => {
    if (!referrerData) return null;
    const max = Math.max(1, ...referrerData.map((r) => safeNumber(r.uniqueVisitors, 0)));
    return (
      <StatCard title="מקורות הפניה" extra={referrerData.length > 0 ? <span className={classes.muted}>{referrerData.length} מקורות</span> : null}>
        {referrerData.length === 0 ? (
          <NoData text="אין נתוני הפניה לטווח התאריכים הנבחר" />
        ) : (
          <DataTable
            data={referrerData}
            rowKey={(r) => r.referrer}
            fit
            columns={[
              {
                key: 'referrer',
                title: 'מקור',
                render: (_, r) => (
                  <span className={classes.refCell}>
                    <span className={classes.url} dir="ltr">
                      {r.referrer}
                    </span>
                    <span className={classes.miniTrack}>
                      <span className={classes.miniFill} style={{ width: `${(safeNumber(r.uniqueVisitors, 0) / max) * 100}%` }} />
                    </span>
                  </span>
                ),
              },
              { key: 'visitors', title: 'מבקרים', width: 100, render: (_, r) => <span className="ui-tabular">{formatNumber(r.uniqueVisitors)}</span> },
              { key: 'events', title: 'אירועים', width: 100, responsive: ['sm'], render: (_, r) => <span className="ui-tabular">{formatNumber(r.totalEvents)}</span> },
            ]}
          />
        )}
      </StatCard>
    );
  };

  // ---- render ------------------------------------------------------------------------
  return (
    <div className={classes.dashboard}>
      {renderHeader()}
      {!error ? renderKpis() : null}

      {error && !loading ? (
        <Section>
          <EmptyState title={error} action={<Button type="primary" onClick={() => loadAllData(startDate, endDate)}>נסה שנית</Button>} />
        </Section>
      ) : (
        // while a new range loads, the old numbers stay but dim — no spinner covering the page
        <div className={classes.body} data-loading={loading || undefined} aria-busy={loading}>
            {renderFunnels()}
            {renderTrendsChart()}
            <div className={classes.twoCol}>
              {renderScrollDistribution()}
              {renderPageEngagement()}
            </div>
            <div className={classes.twoCol}>
              {renderSearchKeywords()}
              {renderReferrerStats()}
            </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
